// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { CartsDataViewContext } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import constants from "@store/constants";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import { tableProps } from "@store/Themes/common/props/table";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";

const Pagination = getWidgetComponent("SavedOrderList/SavedOrderListPagination");
/* Starter Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    getSavedOrdersParameter: state.pages.savedOrderList.getCartsApiParameter,
    language: state.context.session.language,
    savedOrderDetailsLink: getPageLinkByPageType(state, "SavedOrderDetailsPage"),
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListTableStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    orderDateHeader?: DataTableHeaderPresentationProps;
    shipToLabelHeader?: DataTableHeaderPresentationProps;
    subtotalHeader?: DataTableHeaderPresentationProps;
    orderDateCell?: DataTableCellBaseProps;
    shipToLabelCell?: DataTableCellBaseProps;
    subtotalCell?: DataTableCellBaseProps;
    // Starter Custom Code
    loadingOverlay?: LoadingOverlayProps;
    countWrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    tableWrapper?: InjectableCss;
}

export const savedOrderListTableStyles: SavedOrderListTableStyles = {
    loadingOverlay: tableProps.loadingOverlay,
    countWrapper: resultCountProps.wrapper,
    countText: resultCountProps.countText,
    tableWrapper: tableProps.wrapper,
    dataTable: tableProps.dataTable,
    shipToLabelHeader: {
        css: css`
            width: 100%;
        `,
    },
    subtotalHeader: tableProps.amountHeader,
    subtotalCell: tableProps.amountCell,
    noResultsText: typographyGetNoResultsAltProps({ spaced: true }),
};

const styles = savedOrderListTableStyles;

const SavedOrderListTable = ({
    getSavedOrdersParameter,
    updateSearchFields,
    language,
    savedOrderDetailsLink,
    showPricing,
}: Props) => {
    const headerClick = (sortField: string) => {
        const sort = getSavedOrdersParameter.sort === sortField ? `${sortField} DESC` : sortField;
        updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (getSavedOrdersParameter.sort === sortField) {
            sorted = "ascending";
        } else if (getSavedOrdersParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    const baseSavedOrderListDataView = useContext(CartsDataViewContext); // Starter Custom Code
    const savedOrderListDataView = usePersistentDataView(baseSavedOrderListDataView); // Starter Custom Code
    // Starter Removed - LoadingSpinner

    // Starter Moved - noResultsContainer - down

    /* Starter Custom Code - next line - "?" */
    const rows = savedOrderListDataView?.value?.map(savedOrder => {
        return {
            id: savedOrder.id,
            date: savedOrder.orderDate
                ? getLocalizedDateTime({
                      dateTime: new Date(savedOrder.orderDate),
                      language,
                  })
                : "",
            shipToLabel:
                savedOrder.fulfillmentMethod === "PickUp" && savedOrder.defaultWarehouse
                    ? savedOrder.defaultWarehouse.description || savedOrder.defaultWarehouse.name
                    : savedOrder.collectionShipToLabel ?? savedOrder.shipToLabel,
            subtotal: savedOrder.orderSubTotalDisplay,
        };
    });

    /* Starter Custom Code - Start */
    const ordersCount = (savedOrderListDataView as any)?.pagination?.totalItemCount ?? 0;
    const ordersCountLabel = ordersCount === 1 ? translate("Order") : translate(`${"Orders"}`);
    /* Starter Custom Code - End */

    return (
        <DataViewLoadingOverlay dataView={savedOrderListDataView} {...styles.loadingOverlay} /* Starter Custom Code */>
            {!savedOrderListDataView.value?.length /* Starter Custom Code */ ? (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography {...styles.noResultsText}>{translate("No saved orders found.")}</Typography>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.container}>
                    {/* Starter Custom Code - Start */}
                    <StyledWrapper {...styles.countWrapper}>
                        <Typography {...styles.countText}>
                            {ordersCount} {ordersCountLabel}
                        </Typography>
                    </StyledWrapper>
                    {/* Starter Custom Code - End */}
                    <StyledWrapper {...styles.tableWrapper} /* Starter Custom Code */>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader
                                    tight
                                    sorted={sorted("orderDate")}
                                    onSortClick={() => headerClick("orderDate")}
                                    {...styles.orderDateHeader}
                                >
                                    {translate("Date")}
                                </DataTableHeader>
                                <DataTableHeader
                                    tight
                                    sorted={sorted("customerSequence")}
                                    {...styles.shipToLabelHeader}
                                    onSortClick={() => headerClick("customerSequence")}
                                >
                                    {translate("Ship To / Pick Up")}
                                </DataTableHeader>
                                {showPricing && (
                                    <DataTableHeader
                                        tight
                                        sorted={sorted("orderSubtotal")}
                                        {...styles.subtotalHeader}
                                        onSortClick={() => headerClick("orderSubtotal")}
                                    >
                                        {translate("Subtotal") /* Starter Custom Code */}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {/* Starter Custom Code - next line - "?" */}
                                {rows?.map(({ id, date, shipToLabel, subtotal }) => (
                                    <DataTableRow
                                        data-test-selector="savedOrderList_orderLine"
                                        data-test-key={id}
                                        key={id}
                                        {...styles.dataTableRow}
                                    >
                                        <DataTableCell {...styles.orderDateCell}>
                                            {/* Starter Custom Code - Start */}
                                            {createWidgetElement("Basic/PageTypeLink", {
                                                fields: {
                                                    pageType: constants.pageType.savedOrderDetails,
                                                    overrideTitle: date,
                                                    queryString: `cartId=${id}`,
                                                    linkAccessibleText: translate("See Order Details"),
                                                },
                                            })}
                                            {/* Starter Custom Code - End */}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.shipToLabelCell}
                                            data-test-selector="savedOrderLine_shipTo"
                                        >
                                            {shipToLabel}
                                        </DataTableCell>
                                        {showPricing && (
                                            <DataTableCell {...styles.subtotalCell}>{subtotal}</DataTableCell>
                                        )}
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                    <Pagination /* Starter Custom Code */ />
                </StyledWrapper>
            )}
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SavedOrderListTable),
    definition: {
        group: "Saved Order List",
        displayName: "Search Results Table",
        allowedContexts: ["SavedOrderListPage"],
        isSystem: true,
    },
};

export default widgetModule;
